import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Accordion from '@/components/Accordion'
import Button from '@/components/Button'
import DraggableContainer from '@/components/DraggableContainer'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  RecipeGenerationStatus,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import {
  removeStumpShadeRecipeKey,
  setSelectedStumpShadeKey,
  setSelectedStumpShadeStainingBrand,
} from '@/store/stainingStudio'
import { STUMP_SHADE_PANEL_WIDTH } from '@/utils/constants'
import { getOptions } from '@/utils/helper'

import {
  CROWN_BUTTON_TITLE_TOOLTIP,
  STUMP_SHADE_BUTTON_WITH_COLOR,
} from '../utils/constants'

interface Metadata {
  [key: string]: any
}

interface StumpShadePanelProps {
  dimensions: { width: number; height: number }
  setTooltipOpen: (tooltipOpen: boolean) => void
  setTooltipMessage: (tooltipMessage: string) => void
}

const StumpShadePanel: React.FC<StumpShadePanelProps> = ({
  dimensions,
  setTooltipOpen,
  setTooltipMessage,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()

  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata | undefined
    error: any
    isLoading: boolean
  }

  const stainingBrands = useMemo(
    () =>
      metadata ? getOptions(metadata, 'available_aestheticstainingbrands') : [],
    [metadata],
  )

  const selectedStumpShadeKey = useAppSelector(
    (state) => state.stainingStudio.selectedStumpShadeKey,
  )

  const stumpShadeRecipe = useAppSelector(
    (state) => state.stainingStudio.stumpShadeRecipe,
  )

  const selectedValues = useMemo(
    () =>
      [
        Object.keys(stumpShadeRecipe?.[PLACEMENT.LEFT] || {}),
        Object.keys(stumpShadeRecipe?.[PLACEMENT.RIGHT] || {}),
      ].flat(),
    [stumpShadeRecipe],
  )

  const selectedStumpShadeStainingBrand = useAppSelector(
    (state) => state.stainingStudio.selectedStumpShadeBrand,
  )
  const isAbove1280 = useMediaQuery('(min-width: 1280px)')

  return (
    <DraggableContainer
      axis="x"
      bounds="parent"
      defaultPosition={{
        x: dimensions?.width
          ? dimensions?.width - STUMP_SHADE_PANEL_WIDTH / 2
          : 0,
        y: dimensions?.height,
      }}
    >
      <Accordion
        title={
          <LabelWithTooltip
            titleSx={{ fontSize: '18px' }}
            label={t('stainingCorrection.panel.generateRecipe')}
            tooltipText={t('stainingCorrection.panel.recipeTooltip')}
          />
        }
        sx={{
          background: theme.palette.common.black,
          width: isAbove1280 ? STUMP_SHADE_PANEL_WIDTH : '700px',
          transform: 'translate(0%, -100%)',
          '&.MuiPaper-root': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
        accordionSummarySx={{
          padding: '8px 16px',
        }}
        titleSx={{
          fontSize: '20px',
        }}
        accordionDetailsSx={{
          padding: '16px 32px 24px 32px',
        }}
      >
        <Box display="flex" flexGrow={1} gap={'32px'}>
          <Box display="flex">
            <Select
              options={stainingBrands}
              title={t('stainingCorrection.panel.selectStainingBrand')}
              tooltipText={t('stainingCorrection.panel.stainingBrandTooltip')}
              name="staining-brands"
              onFormValueChange={(name, value) => {
                dispatch(setSelectedStumpShadeStainingBrand(value))
              }}
              defaultValue={selectedStumpShadeStainingBrand}
              sx={{
                width: '226px',
              }}
              titleSx={{ fontSize: '20px' }}
              titleSize="small"
            />
          </Box>
          <Box
            sx={{
              borderRight: '1px solid var(--color-border-table)',
              height: 'auto',
            }}
            height="auto"
            display="flex"
          ></Box>
          <Box
            display="flex"
            flexGrow={1}
            gap={2}
            justifyContent="space-around"
          >
            {Object.keys(STUMP_SHADE_BUTTON_WITH_COLOR).map((key, index) => (
              <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                key={index}
              >
                <LabelWithTooltip
                  label={t(`stainingCorrection.panel.${key}`)}
                  tooltipText={t(
                    CROWN_BUTTON_TITLE_TOOLTIP[
                      key as keyof typeof CROWN_BUTTON_TITLE_TOOLTIP
                    ],
                  )}
                  alignContent={'center'}
                  titleSx={{
                    fontSize: '16px',
                  }}
                  tooltipSx={{
                    height: '20px',
                    width: '20px',
                  }}
                  fontSize="16px"
                />
                <Box display="flex" gap={1} marginTop={1}>
                  {Object.keys(
                    STUMP_SHADE_BUTTON_WITH_COLOR[
                      key as keyof typeof STUMP_SHADE_BUTTON_WITH_COLOR
                    ],
                  ).map((name) => (
                    <Button
                      disableRipple
                      variant="contained"
                      key={name}
                      sx={{
                        maxWidth: isAbove1280 ? '128px' : '112.66px',
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                        color:
                          selectedStumpShadeKey === name ||
                          selectedValues?.includes(name)
                            ? theme.palette.common.black
                            : theme.palette.common.white,
                        fontWeight: 400,
                        background:
                          selectedStumpShadeKey === name ||
                          selectedValues?.includes(name)
                            ? STUMP_SHADE_BUTTON_WITH_COLOR[key][name]
                            : '#292929',
                        '&:hover': {
                          background: STUMP_SHADE_BUTTON_WITH_COLOR[key][name],
                          color: theme.palette.common.black,
                        },
                        fontSize: '14px',
                        padding: '8px 10px',
                        maxHeight: '32px',
                      }}
                      label={name}
                      onClick={() => {
                        if (selectedValues?.includes(name)) {
                          dispatch(setSelectedStumpShadeKey(null))
                          dispatch(removeStumpShadeRecipeKey({ key: name }))
                          dispatch(
                            setRecipeGenerationStatus(
                              RecipeGenerationStatus.DEFAULT,
                            ),
                          )
                        } else {
                          dispatch(
                            setRecipeGenerationStatus(
                              RecipeGenerationStatus.GENERATE,
                            ),
                          )
                          dispatch(setSelectedStumpShadeKey(name))
                          setTooltipOpen(true)
                          setTooltipMessage(
                            t('stainingCorrection.panel.firstStep'),
                          )
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Accordion>
    </DraggableContainer>
  )
}

export default StumpShadePanel
